/**
 * useCampaignFormState
 *
 * Internal hook orchestrating form state, validation, and live asset-count
 * query for the CreateCampaignPanel.
 *
 * Live asset-count: 250ms debounced server query via listAssets (count-only,
 * filtered by selected group IDs). Debounce timer cleans up on unmount per
 * CLAUDE.md.
 */
import { Group, useCustomQuery } from '@gripp/shared-logic';
import { useCallback, useEffect, useRef, useState } from 'react';

// Minimal count-only query — listAssets with groups filter returns the deduped
// count of assets in the union of the given group IDs.
const COUNT_ASSETS_IN_GROUPS = `
  query CountAssetsInGroups($filter: ListAssetsFilter!, $page: PageRequest) {
    asset {
      listAssets(filter: $filter, page: $page) {
        count
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CampaignFormState {
  title: string;
  description: string;
  selectedGroups: Group[];
  mediaIds: string[];
  scheduledDate: string;
  scheduledTime: string;
}

export interface CampaignFormActions {
  setTitle: (v: string) => void;
  setDescription: (v: string) => void;
  setSelectedGroups: (groups: Group[]) => void;
  addMediaId: (id: string) => void;
  removeMediaId: (id: string) => void;
  setScheduledDate: (v: string) => void;
  setScheduledTime: (v: string) => void;
  reset: () => void;
}

export interface CampaignFormValidation {
  /** Schedule button: all fields valid + assetCount > 0 */
  canSchedule: boolean;
  /** Save-draft button: title non-empty */
  canSaveDraft: boolean;
  assetCount: number;
  isLoadingAssetCount: boolean;
}

export interface UseCampaignFormStateResult
  extends CampaignFormState,
    CampaignFormActions,
    CampaignFormValidation {}

// ---------------------------------------------------------------------------
// Default form state factory
// ---------------------------------------------------------------------------

const defaultState = (): CampaignFormState => ({
  title: '',
  description: '',
  selectedGroups: [],
  mediaIds: [],
  scheduledDate: '',
  scheduledTime: '',
});

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @param workspaceId — must be defined before the hook is useful. The panel
 *   should render-gate until currentWorkspace?.id is set (per CLAUDE.md guard).
 */
export const useCampaignFormState = (
  workspaceId: string
): UseCampaignFormStateResult => {
  const [state, setState] = useState<CampaignFormState>(defaultState);

  // ---- 250ms debounced group IDs for live asset-count query ----
  const [debouncedGroupIds, setDebouncedGroupIds] = useState<string[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Update debouncedGroupIds when selectedGroups changes (250ms debounce).
  // Return cleanup to clear timer on unmount — required by CLAUDE.md.
  useEffect(() => {
    debounceRef.current = setTimeout(() => {
      setDebouncedGroupIds(state.selectedGroups.map((g) => g.id));
    }, 250);

    return () => {
      if (debounceRef.current !== null) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [state.selectedGroups]);

  // ---- Live deduped asset count ----
  const assetCountEnabled =
    workspaceId.length > 0 && debouncedGroupIds.length > 0;

  const assetCountResult = useCustomQuery({
    query: COUNT_ASSETS_IN_GROUPS,
    variables: {
      filter: {
        // columns is required by ListAssetsFilter — empty array is fine since
        // we only read the top-level `count` field, no per-item enrichment.
        columns: [],
        workspaceId,
        groups: debouncedGroupIds,
      },
      page: { limit: 1, offset: 0 },
    },
    cacheKey: ['campaignAssetCount', workspaceId, ...debouncedGroupIds],
    options: {
      enabled: assetCountEnabled,
      staleTime: 30_000,
    },
  });

  const assetCount: number =
    (assetCountResult.data as { asset?: { listAssets?: { count?: number } } } | undefined)
      ?.asset?.listAssets?.count ?? 0;
  const isLoadingAssetCount = assetCountEnabled && assetCountResult.isLoading;

  // ---- Validation ----
  const { title, description, selectedGroups } = state;
  const canSaveDraft = title.trim().length > 0;
  const canSchedule =
    canSaveDraft &&
    description.trim().length > 0 &&
    selectedGroups.length > 0 &&
    assetCount > 0;

  // ---- Actions ----
  const setTitle = useCallback(
    (v: string) => setState((s) => ({ ...s, title: v })),
    []
  );
  const setDescription = useCallback(
    (v: string) => setState((s) => ({ ...s, description: v })),
    []
  );
  const setSelectedGroups = useCallback(
    (groups: Group[]) => setState((s) => ({ ...s, selectedGroups: groups })),
    []
  );
  const addMediaId = useCallback(
    (id: string) =>
      setState((s) => ({ ...s, mediaIds: [...s.mediaIds, id] })),
    []
  );
  const removeMediaId = useCallback(
    (id: string) =>
      setState((s) => ({ ...s, mediaIds: s.mediaIds.filter((m) => m !== id) })),
    []
  );
  const setScheduledDate = useCallback(
    (v: string) => setState((s) => ({ ...s, scheduledDate: v })),
    []
  );
  const setScheduledTime = useCallback(
    (v: string) => setState((s) => ({ ...s, scheduledTime: v })),
    []
  );
  const reset = useCallback(() => setState(defaultState()), []);

  return {
    ...state,
    setTitle,
    setDescription,
    setSelectedGroups,
    addMediaId,
    removeMediaId,
    setScheduledDate,
    setScheduledTime,
    reset,
    canSchedule,
    canSaveDraft,
    assetCount,
    isLoadingAssetCount,
  };
};
